import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { Newspaper, ArrowLeft, Rss, Eye, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

/* Public newsroom — SEO-targeted studio articles (dev diaries, features, announcements).
   List + article detail in one page, switched by :slug param. */

const EASE = [0.16, 1, 0.3, 1];
const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: EASE } });
const CAT_COLORS = { "dev-diary": "text-nc-blue border-nc-blue/40 bg-nc-blue/10", announcement: "text-nc-orange border-nc-orange/40 bg-nc-orange/10", feature: "text-purple-300 border-purple-400/40 bg-purple-400/10", community: "text-nc-green border-nc-green/40 bg-nc-green/10" };
const MD_COMPONENTS = {
  p: (props) => <p className="mb-4 last:mb-0 text-zinc-300 leading-relaxed" {...props} />,
  h2: (props) => <h2 className="font-sans font-bold uppercase tracking-widest text-lg text-zinc-50 mt-8 mb-3" {...props} />,
  h3: (props) => <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100 mt-6 mb-2" {...props} />,
  strong: (props) => <strong className="text-zinc-100 font-semibold" {...props} />,
  a: (props) => <a className="text-nc-blue hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
  ul: (props) => <ul className="mb-4 flex flex-col gap-1.5" {...props} />,
  li: (props) => <li className="pl-4 relative before:content-['//'] before:absolute before:left-0 before:text-nc-orange text-zinc-300" {...props} />,
  code: (props) => <code className="bg-white/10 px-1.5 py-0.5 text-nc-blue text-[0.85em]" {...props} />,
};

export default function NewsroomPage() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(null);
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cat, setCat] = useState("");
  const [cats, setCats] = useState({});

  useEffect(() => {
    setNotFound(false);
    setArticle(null);
    if (slug) {
      api.get(`/press/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
    } else {
      setArticles(null);
      api.get("/press/articles", { params: { category: cat } }).then((r) => { setArticles(r.data.articles); setCats(r.data.categories); }).catch(() => setArticles([]));
    }
  }, [slug, cat]);

  // ---------------- article detail ----------------
  if (slug) {
    const jsonLd = article ? {
      "@context": "https://schema.org", "@type": "NewsArticle",
      headline: article.title, description: article.excerpt,
      datePublished: article.published_at, author: { "@type": "Organization", name: "Specpixel Entertainment" },
      publisher: { "@type": "Organization", name: "Specpixel Entertainment" },
      keywords: (article.tags || []).join(", "),
    } : null;
    return (
      <div data-testid="newsroom-article-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
        <Seo title={article ? article.title : "Newsroom"} description={article?.excerpt || "New Citizen studio newsroom"} path={`/newsroom/${slug}`} keywords={(article?.tags || []).join(", ")} jsonLd={jsonLd} />
        <SiteNav />
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20 w-full">
          <Link to="/newsroom" data-testid="newsroom-back" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors flex items-center gap-2 w-fit">
            <ArrowLeft className="w-3.5 h-3.5" /> Newsroom
          </Link>
          {notFound && <p data-testid="newsroom-notfound" className="font-mono text-xs text-zinc-500 mt-10 uppercase tracking-[0.25em]">Article not found.</p>}
          {article && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
              <div className="flex items-center gap-3 mt-8 flex-wrap">
                <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${CAT_COLORS[article.category] || CAT_COLORS["dev-diary"]}`}>{cats[article.category] || article.category}</span>
                <span className="font-mono text-[10px] text-zinc-500">{String(article.published_at).slice(0, 10)}</span>
                <span className="font-mono text-[10px] text-zinc-500 flex items-center gap-1"><Eye className="w-3 h-3" /> {article.views}</span>
              </div>
              <h1 data-testid="newsroom-article-title" className="font-sans font-extrabold uppercase tracking-tight text-2xl sm:text-4xl text-white mt-4 leading-tight">{article.title}</h1>
              {article.excerpt && <p className="font-mono text-xs text-zinc-400 leading-relaxed mt-4 border-l-2 border-nc-blue pl-4">{article.excerpt}</p>}
              <div data-testid="newsroom-article-body" className="mt-8 border-t border-white/10 pt-8">
                <ReactMarkdown components={MD_COMPONENTS}>{article.body_md}</ReactMarkdown>
              </div>
              <div className="flex gap-2 flex-wrap mt-8 border-t border-white/10 pt-6">
                {(article.tags || []).map((t) => (
                  <span key={t} className="font-mono text-[9px] tracking-[0.15em] text-zinc-500 border border-white/10 px-2 py-1 flex items-center gap-1"><Tag className="w-3 h-3" />{t}</span>
                ))}
              </div>
            </motion.div>
          )}
        </article>
        <div className="mt-auto"><SiteFooter /></div>
      </div>
    );
  }

  // ---------------- list ----------------
  return (
    <div data-testid="newsroom-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title="Newsroom — New Citizen Dev Diaries & Studio News" description="Dev diaries, feature deep-dives and studio announcements from Specpixel Entertainment — the team building New Citizen, the UE5 living-city roleplay platform." path="/newsroom" keywords="New Citizen game, New Citizen devlog, UE5 roleplay, Specpixel news" />
      <SiteNav />
      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(46,107,255,0.10),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <motion.p {...fade(0)} className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">Specpixel Entertainment</motion.p>
          <motion.h1 {...fade(0.05)} data-testid="newsroom-title" className="font-sans font-extrabold uppercase tracking-tight text-4xl sm:text-5xl text-white mt-4 flex items-center gap-4">
            <Newspaper className="w-9 h-9 text-nc-blue" /> Newsroom
          </motion.h1>
          <motion.p {...fade(0.1)} className="mt-4 max-w-2xl text-base text-zinc-400 leading-relaxed">
            Long-form transmissions from the studio — how the Living Citizens think, how the police AI hunts, and what it takes to build a city that never sleeps.
          </motion.p>
          <motion.a {...fade(0.15)} data-testid="newsroom-rss" href="/api/press/feed.xml" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-nc-orange hover:text-white transition-colors">
            <Rss className="w-3.5 h-3.5" /> RSS feed
          </motion.a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 w-full">
        <div className="flex gap-2 flex-wrap mb-8" data-testid="newsroom-filters">
          <button data-testid="newsroom-cat-all" onClick={() => setCat("")} className={`font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${!cat ? "border-nc-blue/60 text-nc-blue" : "border-white/10 text-zinc-500 hover:border-white/25"}`}>All</button>
          {Object.entries(cats).map(([k, v]) => (
            <button key={k} data-testid={`newsroom-cat-${k}`} onClick={() => setCat(cat === k ? "" : k)} className={`font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${cat === k ? "border-nc-blue/60 text-nc-blue" : "border-white/10 text-zinc-500 hover:border-white/25"}`}>{v}</button>
          ))}
        </div>
        {articles === null && <p className="font-mono text-xs text-zinc-600 uppercase tracking-[0.25em]">Loading…</p>}
        {articles?.length === 0 && <p data-testid="newsroom-empty" className="font-mono text-xs text-zinc-600 uppercase tracking-[0.25em]">First transmission incoming — check back soon.</p>}
        <div className="flex flex-col gap-px bg-white/10 border border-white/10">
          {(articles || []).map((a, i) => (
            <motion.div key={a.id} {...fade(Math.min(i * 0.04, 0.3))}>
              <Link to={`/newsroom/${a.slug}`} data-testid={`newsroom-card-${i + 1}`} className="block bg-[#050505] p-6 sm:p-8 group hover:bg-white/[0.03] transition-colors">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${CAT_COLORS[a.category] || CAT_COLORS["dev-diary"]}`}>{cats[a.category] || a.category}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{String(a.published_at).slice(0, 10)}</span>
                  <span className="font-mono text-[10px] text-zinc-500 flex items-center gap-1"><Eye className="w-3 h-3" /> {a.views}</span>
                </div>
                <h2 className="font-sans font-bold uppercase tracking-tight text-lg sm:text-xl text-zinc-100 mt-3 group-hover:text-white transition-colors">{a.title}</h2>
                {a.excerpt && <p className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-2 max-w-3xl">{a.excerpt}</p>}
                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-blue mt-4 group-hover:tracking-[0.35em] transition-all">Read the transmission →</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
      <div className="mt-auto"><SiteFooter /></div>
    </div>
  );
}
